import type { AiResponse, AnalysisScope, DemoState } from "../types/demo";
import { formatCurrency, getKpis, getMarginLeakage, getScopeLabel } from "./demoSelectors";

const classifyPrompt = (prompt: string): "margin" | "invoice" | "projects" | "cash" | "risk" => {
  const normalized = prompt.toLowerCase();
  if (normalized.includes("margin")) return "margin";
  if (normalized.includes("invoice") || normalized.includes("cashflow")) return "invoice";
  if (normalized.includes("project") || normalized.includes("budget")) return "projects";
  if (normalized.includes("cash")) return "cash";
  return "risk";
};

export const buildAiResponse = (state: DemoState, prompt: string, scope: AnalysisScope, variant: number): AiResponse => {
  const kpis = getKpis(state);
  const mode = classifyPrompt(prompt);
  const scopeLabel = getScopeLabel(state, scope);

  if (mode === "margin") {
    const leakage = getMarginLeakage(state);
    return {
      body: `- Gross margin is at **${kpis.margin.toFixed(1)}%**, down ${Math.abs(kpis.marginDelta).toFixed(1)}pp month-on-month.\n- Material cost spikes in two high-risk projects account for ~41% of margin pressure.\n- Overtime uplift in civil crews is running 18% above plan.\n- Subcontractor rework is concentrated in Northline and Riverpoint.\n- Scope: **${scopeLabel}**`,
      actions: [
        "Renegotiate material pricing for Riverpoint and Northline this week.",
        "Freeze discretionary overtime approvals above 10 hours.",
        "Run a subcontract QA checkpoint before Friday close.",
      ],
      followUps: [
        "Show margin trend for the last 6 months",
        "What invoices are driving cashflow pressure?",
        "Build me a Monday margin report",
      ],
      chartType: "bar",
      chartData: leakage,
    };
  }

  if (mode === "invoice") {
    const overdue = state.invoices.filter(item => item.status === "overdue");
    const buckets = [
      { label: "0-30", value: overdue.filter(item => item.daysOverdue <= 30).length },
      { label: "31-60", value: overdue.filter(item => item.daysOverdue > 30 && item.daysOverdue <= 60).length },
      { label: "61+", value: overdue.filter(item => item.daysOverdue > 60).length },
    ];

    return {
      body: `- There are **${kpis.overdueCount} overdue invoices** totaling **${formatCurrency(kpis.overdueAmount)}**.\n- 62% of the overdue amount sits with 3 customers.\n- Recovery momentum is weak in the 31-60 day bucket.\n- Expect short-term cash tension across payroll windows next fortnight.`,
      actions: [
        "Escalate top 5 overdue accounts to direct outreach.",
        "Offer staged payment plans for 31-60 day balances.",
        "Delay non-critical procurement for seven days.",
      ],
      followUps: [
        "Show top overdue customers",
        "What does this do to runway?",
        "Create a collections report",
      ],
      chartType: "bar",
      chartData: buckets,
    };
  }

  if (mode === "projects") {
    const highRisk = state.projects.filter(project => project.burnRisk === "high").slice(0, 5);
    return {
      body: `- **${kpis.projectsAtRisk} projects** are trending over budget against current burn.\n- Riverpoint and Northline are the primary variance drivers.\n- Project manager load is skewed toward two teams this sprint.\n- Forecasted completion cost exceeds budget by ~${variant % 2 === 0 ? "6.2%" : "5.8%"}.`,
      actions: [
        "Review procurement assumptions with project managers today.",
        "Reallocate top estimator support to high-risk jobs.",
        "Lock weekly variance review into Monday standup.",
      ],
      followUps: [
        "Compare actual vs budget by manager",
        "Show me project-level burn trend",
        "Convert this into weekly report",
      ],
      chartType: "table",
      chartData: highRisk.map(project => ({
        label: project.name,
        value: Math.round((project.actualCost / project.budget) * 100),
      })),
    };
  }

  if (mode === "cash") {
    return {
      body: `- Cash in over the last 7 days is **${formatCurrency(kpis.cashIn7d)}**, below expected pace.\n- Overdue collections are currently the fastest lever to improve liquidity.\n- Two project milestones can be invoiced early with documentation cleanup.\n- Recommend prioritizing debtor calls and milestone invoicing this week.`,
      actions: [
        "Trigger milestone billing for Harbor Logistics and Eastline Schools.",
        "Hold low-priority capex for one billing cycle.",
        "Prioritize collection calls on 10 largest overdue balances.",
      ],
      followUps: [
        "Give me a 14-day cash plan",
        "Who should we call first?",
        "Schedule this as weekly digest",
      ],
      chartType: "line",
      chartData: [
        { label: "Mon", value: 42000 },
        { label: "Tue", value: 35000 },
        { label: "Wed", value: 47000 },
        { label: "Thu", value: 38000 },
        { label: "Fri", value: 52000 },
        { label: "Sat", value: 29000 },
        { label: "Sun", value: 43000 },
      ],
    };
  }

  return {
    body: `- Top financial risk remains margin compression across materials and overtime.\n- Secondary risk is overdue invoice concentration in a small customer cohort.\n- Project burn variance is manageable but needs tighter weekly controls.\n- Overall business remains stable with intervention over next two weeks.`,
    actions: [
      "Review top three risks in Monday leadership meeting.",
      "Assign owners for margin, collections, and project controls.",
      "Track progress in weekly Quantira report.",
    ],
    followUps: ["What changed vs last month?", "Show risk by business unit", "Create an executive brief"],
    chartType: "bar",
    chartData: [
      { label: "Margin", value: 82 },
      { label: "Collections", value: 68 },
      { label: "Projects", value: 61 },
    ],
  };
};

export const parseScheduleText = (text: string): { rule: string; nextRun: string } => {
  const normalized = text.trim().toLowerCase();
  if (normalized === "every monday at 9am") {
    return { rule: "RRULE:FREQ=WEEKLY;BYDAY=MO;BYHOUR=9", nextRun: "2026-02-23T09:00:00.000Z" };
  }
  if (normalized === "every friday at 8am") {
    return { rule: "RRULE:FREQ=WEEKLY;BYDAY=FR;BYHOUR=8", nextRun: "2026-02-20T08:00:00.000Z" };
  }
  return { rule: "RRULE:FREQ=WEEKLY;BYDAY=MO;BYHOUR=9", nextRun: "2026-02-23T09:00:00.000Z" };
};
